import { apiFetch } from "./client";

export type FolderRole = "inbox" | "sent" | "drafts" | "trash" | "junk" | "archive" | "normal";

export type MailFolder = {
  name: string; // roher IMAP-Pfad (für API-Calls)
  anzeige_name: string;
  rolle: FolderRole;
};

export type InboxHeader = {
  uid: string;
  von: string;
  an: string;
  betreff: string;
  datum: string;
  gelesen: boolean;
  hat_anhaenge: boolean;
};

export type InboxAttachment = {
  part_id: string;
  dateiname: string;
  mime_type: string;
  groesse: number;
};

export type InboxMail = {
  uid: string;
  von: string;
  an: string;
  cc: string;
  betreff: string;
  datum: string;
  message_id: string;
  references: string;
  body_text: string;
  body_html: string | null;
  anhaenge: InboxAttachment[];
};

export async function listFolders(): Promise<MailFolder[]> {
  return apiFetch<MailFolder[]>("/api/me/mail/folders");
}

export async function listMessages(
  folder: string,
  opts: { limit?: number; offset?: number; q?: string } = {},
): Promise<InboxHeader[]> {
  const p = new URLSearchParams();
  p.set("folder", folder);
  p.set("limit", String(opts.limit ?? 50));
  p.set("offset", String(opts.offset ?? 0));
  if (opts.q && opts.q.trim()) p.set("q", opts.q.trim());
  return apiFetch<InboxHeader[]>(`/api/me/mail/messages?${p.toString()}`);
}

export async function getMail(uid: string, folder = "INBOX"): Promise<InboxMail> {
  const p = new URLSearchParams({ folder });
  return apiFetch<InboxMail>(`/api/me/mail/message/${encodeURIComponent(uid)}?${p.toString()}`);
}

/** Direktlink für den Anhang-Download. Auth läuft über das HttpOnly-Session-Cookie. */
export function attachmentUrl(uid: string, partId: string, folder = "INBOX"): string {
  const p = new URLSearchParams({ folder });
  return `/api/me/mail/message/${encodeURIComponent(uid)}/attachment/${encodeURIComponent(partId)}?${p.toString()}`;
}

export type ComposePayload = {
  to: string; // Komma-getrennt
  cc?: string;
  betreff: string;
  body: string;
  format: "text" | "markdown";
  in_reply_to?: string;
  references?: string;
  forward_uid?: string;
  forward_folder?: string;
  attachments?: File[];
};

function buildForm(p: ComposePayload | DraftPayload): FormData {
  const fd = new FormData();
  fd.set("to", p.to ?? "");
  fd.set("cc", p.cc ?? "");
  fd.set("betreff", p.betreff ?? "");
  fd.set("body", p.body ?? "");
  fd.set("format", p.format ?? "text");
  if ("in_reply_to" in p && p.in_reply_to) fd.set("in_reply_to", p.in_reply_to);
  if ("references" in p && p.references) fd.set("references", p.references);
  if ("forward_uid" in p && p.forward_uid) {
    fd.set("forward_uid", p.forward_uid);
    fd.set("forward_folder", p.forward_folder ?? "INBOX");
  }
  if ("replace_uid" in p && p.replace_uid) fd.set("replace_uid", p.replace_uid);
  for (const f of p.attachments ?? []) fd.append("attachments", f);
  return fd;
}

export async function sendMail(payload: ComposePayload): Promise<{ ok: boolean; detail: string }> {
  return apiFetch<{ ok: boolean; detail: string }>("/api/me/mail/send", {
    method: "POST",
    body: buildForm(payload),
  });
}

export type DraftPayload = {
  to?: string;
  cc?: string;
  betreff: string;
  body: string;
  format: "text" | "markdown";
  replace_uid?: string;
  attachments?: File[];
};

export async function saveDraft(payload: DraftPayload): Promise<{ ok: boolean; detail: string }> {
  return apiFetch<{ ok: boolean; detail: string }>("/api/me/mail/draft", {
    method: "POST",
    body: buildForm(payload),
  });
}

export async function deleteDraft(uid: string): Promise<{ ok: boolean; detail: string }> {
  return apiFetch<{ ok: boolean; detail: string }>(`/api/me/mail/draft/${encodeURIComponent(uid)}`, {
    method: "DELETE",
  });
}

export async function deleteMail(uid: string, folder = "INBOX"): Promise<{ ok: boolean; detail: string }> {
  const p = new URLSearchParams({ folder });
  return apiFetch<{ ok: boolean; detail: string }>(
    `/api/me/mail/message/${encodeURIComponent(uid)}?${p.toString()}`,
    { method: "DELETE" },
  );
}
